import { getFontCss, styleConstants } from '@paywithextend/components'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { selectErrorDetailMapFromState } from 'src/app/RegisterAccount/redux/registerAccount.selectors'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import { ThankYouForSettingUpYourOrgToastMessage } from 'src/app/Toast/Toast'
import OrganizationFormClipped from 'src/assets/svgs/Organization-Form-Clipped.svg'
import Popup from 'src/components/Popup/Popup'
import RegisterOrganizationForm from 'src/components/RegisterOrganizationForm/RegisterOrganizationForm'
import Tooltip from 'src/components/Tooltip/Tooltip'
import { FormFieldsValidation, useFormFields } from 'src/hooks'
import Trns from 'src/localization/Trns'
import { closeModal } from 'src/navigation/Lnk'
import {
  createAbandonPostOrganizationAction,
  postCreateOrganizationAndOpenToastAndClosePopup,
} from 'src/redux/actions/data/organizations.actions'
import { selectIndustryOptions } from 'src/redux/selectors/data/config.selectors'
import { selectMostRecentlyCreatedCreditCardListModelFromState } from 'src/redux/selectors/data/creditCards.selectors'
import {
  selectMostRecentlyCreatedCreditCardOrganizationListModelFromState,
  selectOrganizationTopLevelErrorFromState,
} from 'src/redux/selectors/data/organizations.selectors'
import { stringIsEmpty } from 'src/utils/validation/string.validation'
import { css } from 'styled-components'

export type RegisterOrganizationFields = {
  name?: string
  industry?: string
}

const RegisterOrganization: React.FC = () => {
  const errorDetailMap = useSelector(selectErrorDetailMapFromState)
  const industryOptions = useSelector(selectIndustryOptions)
  const { analytics } = useAnalyticsContext()
  const mostRecentCreditCard = useSelector(
    selectMostRecentlyCreatedCreditCardListModelFromState,
  )
  const mostRecentCreditCardOrganization = useSelector(
    selectMostRecentlyCreatedCreditCardOrganizationListModelFromState,
  )
  const organizationTopLevelError = useSelector(
    selectOrganizationTopLevelErrorFromState,
  )
  const dispatch = useDispatch()

  const registerOrganizationFieldsValidation =
    (): FormFieldsValidation<RegisterOrganizationFields> => ({
      name: [stringIsEmpty()],
      industry: [stringIsEmpty()],
    })
  const {
    fields: organizationFlatFields,
    fieldsErrors: organizationFlatFieldsErrors,
    onFieldChange: organizationFlatFieldChange,
    setErrorsAndReturnDidPassValidation: organizationFlatFieldsSetErrors,
  } = useFormFields<RegisterOrganizationFields>(
    {
      name: mostRecentCreditCard?.companyName,
      industry: mostRecentCreditCardOrganization?.industry,
    },
    registerOrganizationFieldsValidation,
    errorDetailMap,
    undefined,
  )

  const onRegisterOrganizationSubmit = (): void => {
    const organizationFlatFieldsPassedValidation =
      organizationFlatFieldsSetErrors()
    if (organizationFlatFieldsPassedValidation) {
      dispatch(
        postCreateOrganizationAndOpenToastAndClosePopup(
          organizationFlatFields,
          undefined,
          () => {
            dispatch(closeModal())
            dispatch(
              createSetIsToastOpen(true, {
                toastMessage: <ThankYouForSettingUpYourOrgToastMessage />,
              }),
            )
          },
        ),
      )
    }
  }

  const onRegisterOrganizationClose = (): void => {
    dispatch(createAbandonPostOrganizationAction())
    analytics?.track('setup org - abandon setup org - button press', {
      screen: '/setuporg',
      interaction: 'button press',
      intent: 'abandon setup org',
    })
  }

  return (
    <Popup
      onClose={onRegisterOrganizationClose}
      css={css`
        display: flex;
        flex-direction: column;
        position: relative;
      `}
    >
      <img
        src={OrganizationFormClipped}
        alt={''}
        css={css`
          position: absolute;
          top: 0;
          right: 0;
          border-top-right-radius: 10px;
        `}
      />
      <div
        css={css`
          display: flex;
          margin-bottom: 20px;
        `}
      >
        <span
          css={css`
            ${getFontCss(
              32,
              styleConstants.colors.black,
              styleConstants.fontFamilies.medium,
            )}
          `}
        >
          <Trns source={'Set up your organization'} />
        </span>
        <Tooltip openLeft={false}>
          <Trns
            source={
              'Setting up an organization opens up the ability to invite members and assign roles and permissions.'
            }
          />
        </Tooltip>
      </div>
      <RegisterOrganizationForm
        onFlatFieldChange={organizationFlatFieldChange}
        flatFields={organizationFlatFields}
        flatFieldsErrors={organizationFlatFieldsErrors}
        industryOptions={industryOptions}
        onSubmit={onRegisterOrganizationSubmit}
        topLevelError={organizationTopLevelError}
      />
    </Popup>
  )
}

export default RegisterOrganization
